import { useState } from "react";
import { ActionButton, Dialog, DialogContent } from "@jhool-io/fe-components";
import { useParams } from "react-router-dom";
import { useGetDiagnosisCodesDisplay } from "../../../../hooks/helpers";
import {
    IAddendumDetails,
    SessionLocations,
} from "../../../../modules/notes/types/notes.types";
import { DOCUMENTATION_REQUEST_NOTE_MED_LABELS } from "../../../../utils/constants";

import {
    removeEnumUnderscore,
    // convertTimeTo12HoursFormat,
    formatDate,
    cn,
    displayNameInRightFormat,
} from "../../../../utils/helpers";
import NoteContentItem from "../NoteContentItem/NoteContentItem";
import styles from "../../../../modules/notes/components/NoteContent/NoteContent.module.scss";
import CheckIcon from "../../../../icons/Check";
import { IViewFile } from "../../../../utils/types";
import ViewFile from "../../../../shared-ui/ViewFile/ViewFile";
import useGetDiagnosisCodeToDisplay from "../../../../modules/notes/hooks";
import { useFetchSessionNote } from "../../../../hooks/queries/note";

interface ViewDocumentationRequestNoteMedProps {
    noteContent: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: any;
    };
    fieldAddendums: {
        [key: string]: IAddendumDetails[];
    } | null;
}

export default function ViewDocumentationRequestNoteMed({
    noteContent,
    fieldAddendums,
}: ViewDocumentationRequestNoteMedProps) {
    const [docInView, setDocInView] = useState<IViewFile | null>(null);

    const diagonosisCodes = useGetDiagnosisCodesDisplay(
        noteContent.diagnosis_codes
    );

    const params = useParams();
    const noteId = params.noteId as string;
    const clientId = params.clientId as string;

    const { data } = useFetchSessionNote(
        clientId,
        noteId,
        Boolean(clientId) && Boolean(noteId)
    );

    const recordOfDisclosureOfPhiEphiDate =
        noteContent.record_of_disclosure_of_phi_ephi
            ? formatDate(noteContent.record_of_disclosure_of_phi_ephi, true)
            : "--";

    const isCheckboxChecked = (checkboxValue: string, key: string) => {
        return Boolean(
            noteContent?.[key]?.find((value: string) => value === checkboxValue)
        );
    };

    return (
        <>
            <NoteContentItem
                headerText={
                    DOCUMENTATION_REQUEST_NOTE_MED_LABELS.session_location
                }
                fieldId="session_location"
                addendums={fieldAddendums?.session_location}
                content={
                    noteContent.session_location
                        ? removeEnumUnderscore(noteContent.session_location)
                        : "--"
                }
            />
            {noteContent.session_location === SessionLocations.Telehealth && (
                <>
                    <NoteContentItem
                        headerText={
                            DOCUMENTATION_REQUEST_NOTE_MED_LABELS.location_of_the_client
                        }
                        fieldId="location_of_the_client"
                        addendums={fieldAddendums?.location_of_the_client}
                        content={
                            noteContent.location_of_the_client
                                ? removeEnumUnderscore(
                                      noteContent.location_of_the_client
                                  )
                                : "--"
                        }
                    />
                    {noteContent.location_of_the_client === "other" && (
                        <NoteContentItem
                            headerText={
                                DOCUMENTATION_REQUEST_NOTE_MED_LABELS.other_location_of_the_client
                            }
                            fieldId="other_location_of_the_client"
                            addendums={
                                fieldAddendums?.other_location_of_the_client
                            }
                            content={
                                noteContent.other_location_of_the_client || "--"
                            }
                        />
                    )}
                    <NoteContentItem
                        headerText={
                            DOCUMENTATION_REQUEST_NOTE_MED_LABELS.location_of_the_provider
                        }
                        fieldId="location_of_the_provider"
                        addendums={fieldAddendums?.location_of_the_provider}
                        content={
                            noteContent.location_of_the_provider
                                ? removeEnumUnderscore(
                                      noteContent.location_of_the_provider
                                  )
                                : "--"
                        }
                    />
                    {noteContent.location_of_the_provider === "other" && (
                        <NoteContentItem
                            headerText={
                                DOCUMENTATION_REQUEST_NOTE_MED_LABELS.other_location_of_the_provider
                            }
                            fieldId="other_location_of_the_provider"
                            addendums={
                                fieldAddendums?.other_location_of_the_provider
                            }
                            content={
                                noteContent.other_location_of_the_provider ||
                                "--"
                            }
                        />
                    )}
                    <NoteContentItem
                        headerText={
                            DOCUMENTATION_REQUEST_NOTE_MED_LABELS.consent_was_received_for_the_teletherapy_session
                        }
                        fieldId="consent_was_received_for_the_teletherapy_session"
                        addendums={
                            fieldAddendums?.consent_was_received_for_the_teletherapy_session
                        }
                        content={
                            noteContent.consent_was_received_for_the_teletherapy_session
                                ? "Yes"
                                : "No"
                        }
                    />
                    <NoteContentItem
                        headerText={
                            DOCUMENTATION_REQUEST_NOTE_MED_LABELS.the_session_was_conducted_via_video
                        }
                        fieldId="the_session_was_conducted_via_video"
                        addendums={
                            fieldAddendums?.the_session_was_conducted_via_video
                        }
                        content={
                            noteContent.the_session_was_conducted_via_video
                                ? "Yes"
                                : "No"
                        }
                    />
                </>
            )}

            {docInView ? (
                <Dialog open={Boolean(docInView)}>
                    <DialogContent
                        title={
                            docInView?.document_name ??
                            "Note content attachment"
                        }
                        handleCloseDialog={() => {
                            setDocInView(null);
                        }}
                        showFooter={false}
                    >
                        <ViewFile
                            url={docInView.url}
                            contentType={
                                docInView?.metadata?.content_type ?? ""
                            }
                            title={
                                docInView?.document_name ??
                                "Note content attachment"
                            }
                        />
                    </DialogContent>
                </Dialog>
            ) : null}

            <NoteContentItem
                headerText={
                    DOCUMENTATION_REQUEST_NOTE_MED_LABELS.diagnosis_codes
                }
                fieldId="diagnosis_codes"
                addendums={fieldAddendums?.diagnosis_codes}
                content={
                    diagonosisCodes && Array.isArray(diagonosisCodes) ? (
                        <div className={styles.checkboxes}>
                            {diagonosisCodes.map((code) => (
                                <div className={styles.checkbox} key={code}>
                                    <span
                                        className={cn(styles.checkbox_wrap, {
                                            [styles.checked]: true,
                                        })}
                                    >
                                        <CheckIcon className="stroke-text" />
                                    </span>
                                    {code}
                                </div>
                            ))}
                        </div>
                    ) : (
                        "--"
                    )
                }
            />
            <NoteContentItem
                headerText={
                    DOCUMENTATION_REQUEST_NOTE_MED_LABELS.secondary_diagnosis
                }
                fieldId="secondary_diagnosis"
                addendums={fieldAddendums?.secondary_diagnosis}
                content={useGetDiagnosisCodeToDisplay(
                    noteContent.secondary_diagnosis
                )}
            />
            <NoteContentItem
                headerText={
                    DOCUMENTATION_REQUEST_NOTE_MED_LABELS.tertiary_diagnosis
                }
                fieldId="tertiary_diagnosis"
                addendums={fieldAddendums?.tertiary_diagnosis}
                content={useGetDiagnosisCodeToDisplay(
                    noteContent.tertiary_diagnosis
                )}
            />
            <NoteContentItem
                headerText={
                    DOCUMENTATION_REQUEST_NOTE_MED_LABELS.additional_diagnosis_one
                }
                fieldId="additional_diagnosis_one"
                addendums={fieldAddendums?.additional_diagnosis_one}
                content={useGetDiagnosisCodeToDisplay(
                    noteContent.additional_diagnosis_one
                )}
            />
            <NoteContentItem
                headerText={
                    DOCUMENTATION_REQUEST_NOTE_MED_LABELS.additional_diagnosis_two
                }
                fieldId="additional_diagnosis_two"
                addendums={fieldAddendums?.additional_diagnosis_two}
                content={useGetDiagnosisCodeToDisplay(
                    noteContent.additional_diagnosis_two
                )}
            />
            <NoteContentItem
                headerText={
                    DOCUMENTATION_REQUEST_NOTE_MED_LABELS.record_of_disclosure_of_phi_ephi
                }
                fieldId="record_of_disclosure_of_phi_ephi"
                addendums={fieldAddendums?.record_of_disclosure_of_phi_ephi}
                content={recordOfDisclosureOfPhiEphiDate}
            />
            <NoteContentItem
                headerText={
                    DOCUMENTATION_REQUEST_NOTE_MED_LABELS.disclosure_to_whom
                }
                fieldId="disclosure_to_whom"
                addendums={fieldAddendums?.disclosure_to_whom}
                content={noteContent.disclosure_to_whom || "--"}
            />
            <NoteContentItem
                headerText={
                    DOCUMENTATION_REQUEST_NOTE_MED_LABELS.purpose_of_disclosure
                }
                fieldId="purpose_of_disclosure"
                addendums={fieldAddendums?.purpose_of_disclosure}
                content={noteContent.purpose_of_disclosure || "--"}
            />
            <NoteContentItem
                headerText={
                    DOCUMENTATION_REQUEST_NOTE_MED_LABELS.description_disclosure_purpose_of_disclosure
                }
                fieldId="description_disclosure_purpose_of_disclosure"
                addendums={
                    fieldAddendums?.description_disclosure_purpose_of_disclosure
                }
                content={
                    noteContent.description_disclosure_purpose_of_disclosure ||
                    "--"
                }
            />
            <NoteContentItem
                headerText={
                    DOCUMENTATION_REQUEST_NOTE_MED_LABELS.type_disclosed
                }
                fieldId="type_disclosed"
                addendums={fieldAddendums?.type_disclosed}
                content={
                    noteContent?.type_disclosed?.length > 0 ? (
                        <div className={styles.checkboxes}>
                            {isCheckboxChecked(
                                "Treatment Record",
                                "type_disclosed"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Treatment Record
                                </div>
                            )}
                            {isCheckboxChecked(
                                "Payment Information",
                                "type_disclosed"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Payment Information
                                </div>
                            )}
                            {isCheckboxChecked(
                                "Healthcare Operation",
                                "type_disclosed"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Healthcare Operation
                                </div>
                            )}
                        </div>
                    ) : (
                        "--"
                    )
                }
            />
            <NoteContentItem
                headerText={
                    DOCUMENTATION_REQUEST_NOTE_MED_LABELS.type_disclosed
                }
                fieldId="type_disclosed"
                addendums={fieldAddendums?.type_disclosed}
                content={
                    noteContent?.type_disclosed?.length > 0 ? (
                        <div className={styles.checkboxes}>
                            {isCheckboxChecked(
                                "Fax",
                                "how_disclosure_is_made"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    F
                                </div>
                            )}
                            {isCheckboxChecked(
                                "Telephone",
                                "how_disclosure_is_made"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Telephone
                                </div>
                            )}
                            {isCheckboxChecked(
                                "Electronic",
                                "how_disclosure_is_made"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Electronic
                                </div>
                            )}
                            {isCheckboxChecked(
                                "Mail",
                                "how_disclosure_is_made"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Mail
                                </div>
                            )}
                            {isCheckboxChecked(
                                "Other",
                                "how_disclosure_is_made"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Other
                                </div>
                            )}
                        </div>
                    ) : (
                        "--"
                    )
                }
            />
            <NoteContentItem
                headerText={
                    DOCUMENTATION_REQUEST_NOTE_MED_LABELS.other_how_disclosure_is_made
                }
                fieldId="other_how_disclosure_is_made"
                addendums={fieldAddendums?.other_how_disclosure_is_made}
                content={noteContent.other_how_disclosure_is_made || "--"}
            />
            {/*
            <NoteContentItem
                headerText={DOCUMENTATION_REQUEST_NOTE_MED_LABELS.start_time}
                fieldId="start_time"
                addendums={fieldAddendums?.start_time}
                content={
                    noteContent.start_time
                        ? convertTimeTo12HoursFormat(
                              noteContent.start_time as string
                          )
                        : "--"
                }
            />
            <NoteContentItem
                headerText={DOCUMENTATION_REQUEST_NOTE_MED_LABELS.end_time}
                fieldId="end_time"
                addendums={fieldAddendums?.end_time}
                content={
                    noteContent.end_time
                        ? convertTimeTo12HoursFormat(
                              noteContent.end_time as string
                          )
                        : "--"
                }
            />
            */}
            <NoteContentItem
                headerText={DOCUMENTATION_REQUEST_NOTE_MED_LABELS.files}
                fieldId="files"
                addendums={fieldAddendums?.files}
                content={
                    <div>
                        {data?.data?.note_content_documents &&
                        data?.data?.note_content_documents.length ? (
                            <div className="flex gap-[var(--spacing-items)] flex-wrap">
                                {data?.data?.note_content_documents.map(
                                    (doc) => (
                                        <ActionButton
                                            key={doc.note_content_document_id}
                                            variant="can-hover"
                                            size="auto"
                                            className="h-7 !text-text text-caption underline"
                                            actionTitle="View file"
                                            onClick={() => {
                                                setDocInView(doc);
                                            }}
                                        >
                                            <span>{`${displayNameInRightFormat(
                                                {
                                                    firstName:
                                                        doc.uploaded_by
                                                            .first_name,
                                                    lastName:
                                                        doc.uploaded_by
                                                            .last_name,
                                                },
                                                false
                                            )} -  ${doc.document_name}`}</span>
                                        </ActionButton>
                                    )
                                )}
                            </div>
                        ) : (
                            "--"
                        )}
                    </div>
                }
            />
        </>
    );
}
